import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, Dimensions, ScrollView, Alert, Linking, Platform, useWindowDimensions } from 'react-native';
import { Surface, Appbar, Card, Text, Button, useTheme, Menu, List, Divider, IconButton, Portal, Modal, Chip } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Sidebar, SIDEBAR_WIDTH } from '../../src/components/Sidebar';
import { useUser } from '../../src/hooks/useUser';
import { useProfile } from '../../src/hooks/useProfile';
import { getCompanyLicenses, apiFetch, cancelSubscription, reactivateSubscription, createBillingPortalSession } from '../../src/api/apiService';
import { API_BASE, API_ENDPOINTS, APP_ORIGIN } from '../../src/api/config';
import { EditSubscriptionModal } from '../../src/components/EditSubscriptionModal';
import { ChangePaymentMethodModal } from '../../src/components/ChangePaymentMethodModal';
import { UnifiedBuyLicensesModal } from '../../src/components/UnifiedBuyLicensesModal';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { PAYMENT_METHOD_ICONS, getStatusColor, formatDate, formatCurrency } from '../../src/utils/subscriptionUtils';


const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface PaymentMethodResponse {
  client_secret: string;
}

interface Invoice {
  id: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  due_date: string;
  paid_at: string;
  invoice_pdf: string;
  hosted_invoice_url: string;
  created_at: string;
}

interface InvoiceResponse {
  invoices: Invoice[];
  total_count: number;
  page: number;
  total_pages: number;
}

export default function ManageLicenses() {
  const { t, i18n } = useTranslation('manageLicenses');
  const theme = useTheme();
  const { width, height } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const { handleLogout, userData } = useUser();
  const router = useRouter();
  const [buyModalVisible, setBuyModalVisible] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [, forceUpdate] = useState({});
  
  // Use the new profile hook
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<any>(null);
  const [menuVisibleId, setMenuVisibleId] = useState<number | null>(null);
  const [changingPaymentMethod, setChangingPaymentMethod] = useState<any>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const locale = i18n.language === 'pt_BR' ? 'pt-BR' : i18n.language === 'pt' ? 'pt-PT' : 'en-US';

  // Force re-render when window width changes
  useEffect(() => {
    const handleResize = () => {
      forceUpdate({});
    };

    if (Platform.OS === 'web') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Update sidebar state when screen size changes
  useEffect(() => {
    if (isLargeScreen) {
      setSidebarOpen(false); // Hide mobile sidebar on large screens
    }
  }, [isLargeScreen]);

  const getLocalizedProductName = (sub: any) => {
    const name = sub?.product?.name;
    const key = sub?.product?.metadata?.i18n_key;
    if (key) {
      return t(`products.${key}`, name || t('defaultProductName', 'Controle de Horas'));
    }
    return name || t('defaultProductName', 'Controle de Horas');
  };

  // --- Request guards to avoid duplicate network calls ---
  const isFetchingLicensesRef = useRef(false);
  const lastLicensesFetchAtRef = useRef<number>(0);
  const isFetchingInvoicesRef = useRef(false);

  const handleCancel = async (subscriptionId: string) => {
    try {
      await cancelSubscription(subscriptionId);
      fetchLicenses();
    } catch (error) {
      console.error('Error canceling subscription:', error);
    }
  };

  const handleReactivate = async (subscriptionId: string) => {
    try {
      await reactivateSubscription(subscriptionId);
      fetchLicenses();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
    }
  };

  const handleChangeLicenses = (sub: any) => {
    // Open Stripe billing portal directly instead of modal
    handleManageSubscription(sub);
  };

  const handleChangePaymentMethod = (subscription: any) => {
    setChangingPaymentMethod(subscription);
  };

  const fetchLicenses = async () => {
    const now = Date.now();
    // Throttle: skip if fetched within the last 3s
    if (now - lastLicensesFetchAtRef.current < 3000) return;
    if (isFetchingLicensesRef.current) return;
    isFetchingLicensesRef.current = true;
    setLoading(true);
    try {
      const response = await getCompanyLicenses();
      if (response.ok) {
        setLicenses(response.data.subscriptions);
        setSummary(response.data.summary);
        lastLicensesFetchAtRef.current = Date.now();
      }
    } catch (error) {
      console.error('Error fetching licenses:', error);
    } finally {
      isFetchingLicensesRef.current = false;
      setLoading(false);
    }
  };

  const fetchInvoices = async (subscriptionId: string, page: number = 1) => {
    if (isFetchingInvoicesRef.current) return;
    isFetchingInvoicesRef.current = true;
    setLoadingInvoices(true);
    try {
      const response = await apiFetch<InvoiceResponse>(
        `${API_ENDPOINTS.INVOICES(subscriptionId)}?page=${page}&per_page=${ITEMS_PER_PAGE}`
      );
      console.log('Invoice API Response:', response); // Debug log
      
      if (response.ok && response.data) {
        const { invoices, total_count, page: currentPage, total_pages } = response.data;
        console.log('Parsed invoice data:', { invoices, total_count, currentPage, total_pages }); // Debug log
        
        setInvoices(invoices || []);
        setTotalPages(total_pages || 1);
        setTotalInvoices(total_count || 0);
        setCurrentPage(currentPage || 1);
      } else {
        console.error('Invalid response format:', response);
        Alert.alert(t('error', 'Error'), t('errorFetchingInvoices', 'Error fetching invoices'));
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      Alert.alert(t('error', 'Error'), t('errorFetchingInvoices', 'Error fetching invoices'));
    } finally {
      isFetchingInvoicesRef.current = false;
      setLoadingInvoices(false);
    }
  };

  const handleViewInvoices = (subscription: any) => {
    setSelectedSubscription(subscription);
    setCurrentPage(1);
    fetchInvoices(subscription.stripe_subscription_id, 1);
    setShowInvoices(true);
  };

  const handleOpenInvoice = async (url: string) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      console.error('Error opening invoice:', error);
      Alert.alert(t('error', 'Error'), t('errorOpeningInvoice', 'Error opening invoice'));
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchInvoices(selectedSubscription.stripe_subscription_id, newPage);
    }
  };

  // Handle opening Stripe billing portal in the same page
  const handleManageSubscription = async (sub: any) => {
    try {
      const frontendReturnUrl = Platform.OS === 'web'
        ? (typeof window !== 'undefined' ? window.location.origin : APP_ORIGIN) + '/dashboard/manage-licenses'
        : `${APP_ORIGIN}/dashboard/manage-licenses`;
      const stripeApiReturnUrl = `${API_BASE}/api/subscriptions/billing_portal/`;
      const response = await createBillingPortalSession(frontendReturnUrl, stripeApiReturnUrl, Platform.OS !== 'web');
      if (response.ok && response.data.url) {
        // Open in the same page/tab instead of popup
        await Linking.openURL(response.data.url);
      } else {
        Alert.alert(t('error', 'Error'), t('errorManageSubscription', 'Error opening billing portal'));
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      Alert.alert(t('error', 'Error'), t('errorManageSubscription', 'Error opening billing portal'));
    }
  };

  const filteredLicenses = showInactive
    ? licenses
    : licenses.filter(sub => ['active', 'trialing'].includes(sub.status));

  // Helper function to determine if a license is GPS or Hours
  const isGPSLicense = (sub: any) => {
    const i18nKey = sub?.product?.metadata?.i18n_key;
    const productName = sub?.product?.name?.toLowerCase() || '';
    return i18nKey === 'gps_tracking' || productName.includes('gps') || productName.includes('tracking');
  };

  const isComboLicense = (sub: any) => {
    return (sub?.product?.metadata?.feature || '').toLowerCase() === 'combo';
  };

  // Separate licenses by type
  const comboLicenses = filteredLicenses.filter(isComboLicense);
  const gpsLicenses = filteredLicenses.filter(sub => isGPSLicense(sub) && !isComboLicense(sub));
  const hoursLicenses = filteredLicenses.filter(sub => !isGPSLicense(sub) && !isComboLicense(sub));

  useEffect(() => {
    // Fetch once on mount; avoid tying to window size to prevent cascaded fetches on resize
    fetchLicenses();
  }, []);

  // Force rerender when screen size changes significantly
  const screenSizeKey = `${width}x${height}`;

  // Helper function to render a license card (desktop version)
  const renderDesktopLicenseCard = (sub: any) => {
    const pm = sub.payment_method;
    const def = PAYMENT_METHOD_ICONS[pm?.type] || { lib: 'mc', name: 'credit-card-outline' };
    const IconComp = def.lib === 'fa5' ? FontAwesome5 : MaterialCommunityIcons;
    const iconProps = def.lib === 'fa5' ? { brand: true } : {};
    
    return (
      <Card key={sub.id} style={styles.subscriptionCard}>
        <Card.Content>
          <View style={styles.desktopRowTop}>
            <View style={styles.cardColProduct}>
              <Text style={styles.fieldLabel}>{t('product','Produto')}</Text>
              <Text style={styles.productText}>
                {getLocalizedProductName(sub)} × {sub.licenses}
              </Text>
              {isComboLicense(sub) && (
                <Chip mode="flat" style={{ alignSelf: 'flex-start', marginTop: 6 }} icon="link-variant">
                  {t('onboarding:planType.combo', 'Combo (Hours + GPS)')}
                </Chip>
              )}
            </View>
            <View style={styles.cardColFrequency}>
              <Text style={styles.fieldLabel}>{t('frequency','Frequência')}</Text>
              <Text style={styles.frequencyText}>
                {sub.price ? (sub.price.interval === 'year' ? t('annual','Faturamento anual') : t('monthly','Faturamento mensal')) : t('manual','Faturamento Manual')}
              </Text>
            </View>
            <View style={styles.cardColPaymentType}>
              <Text style={[styles.fieldLabel, { textAlign: 'center' }]}>{t('paymentMethod','Método de pagamento')}</Text>
              <View style={{ alignItems: 'center', marginTop: 6 }}>
                {pm && (
                  <IconComp name={def.name} size={20} color={theme.colors.primary} {...iconProps} />
                )}
              </View>
            </View>
            <View style={styles.cardColStatus}>
              <Text style={[styles.fieldLabel, { textAlign: 'center' }]}>{t('status','Status')}</Text>
              <View style={styles.statusRowCentered}>
                <View style={[styles.statusDot, { backgroundColor: getStatusColor(sub.status, theme) }]} />
                <Text style={styles.statusText}>
                  {String(t(`statusLabels.${sub.status}`, sub.status.charAt(0).toUpperCase() + sub.status.slice(1)))}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.desktopRowBottom}>
            <View style={styles.nextInvoiceArea}>
              <Text style={styles.fieldLabel}>{t('nextInvoice','Próxima fatura')}</Text>
              {sub.cancel_at_period_end ? (
                <Text style={[styles.nextInvoiceText, { color: theme.colors.error }]}> 
                  {t('cancelAtPeriodEnd','Cancels on')} {formatDate(sub.current_period_end, locale)}
                </Text>
              ) : sub.status === 'trialing' ? (
                <Text style={[styles.nextInvoiceText, { color: theme.colors.secondary || theme.colors.primary }]}> 
                  {t('trialExpiresOn','Trial expires on')} {formatDate(sub.next_payment_date, locale)} {t('invoiceAmount','no valor de')} {formatCurrency((sub.price?.unit_amount || 0) * sub.licenses, sub.price?.currency || 'BRL', locale)}
                </Text>
              ) : !sub.next_payment_date && !sub.price ? (
                <Text style={styles.nextInvoiceText}> </Text>
              ) : (
                <Text style={styles.nextInvoiceText}>
                  {t('nextInvoiceOn','Next invoice on')} {formatDate(sub.next_payment_date, locale)} {t('invoiceAmount','no valor de')} {formatCurrency((sub.price?.unit_amount || 0) * sub.licenses, sub.price?.currency || 'BRL', locale)}
                </Text>
              )}
            </View>
            <View style={styles.actionsArea}>
              {sub.price && (
                <Button
                  mode="text"
                  onPress={() => handleChangeLicenses(sub)}
                  compact
                  icon="open-in-new"
                  style={[styles.actionButton, { alignSelf: 'flex-end' }]}
                  contentStyle={{ justifyContent: 'center', alignItems: 'center' }}
                  labelStyle={{ textAlignVertical: 'center', lineHeight: 20 }}
                >
                  {t('manageSubscription', 'Gerenciar assinatura')}
                </Button>
              )}
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  };

  // Helper function to render a license card (mobile version)
  const renderMobileLicenseCard = (sub: any) => {
    const pm = sub.payment_method;
    const def = PAYMENT_METHOD_ICONS[pm?.type] || { lib: 'mc', name: 'credit-card-outline' };
    const IconComp = def.lib === 'fa5' ? FontAwesome5 : MaterialCommunityIcons;
    const iconProps = def.lib === 'fa5' ? { brand: true } : {};
    
    return (
      <Card key={sub.id} style={styles.subscriptionCard}>
        <Card.Title
          title={`${getLocalizedProductName(sub)} × ${sub.licenses}`}
          left={props => pm && <IconComp {...props} name={def.name} size={20} color={theme.colors.primary} {...iconProps} />}
        />
        <Card.Content>
          <View style={{ gap: 12 }}>
            {isComboLicense(sub) && (
              <Chip mode="flat" style={{ alignSelf: 'flex-start' }} icon="link-variant">
                {t('onboarding:planType.combo', 'Combo (Hours + GPS)')}
              </Chip>
            )}
            <View>
              <Text style={styles.fieldLabel}>{t('frequency','Frequência')}</Text>
              <Text style={styles.frequencyText}>
                {sub.price ? (sub.price.interval === 'year' ? t('annual','Faturamento anual') : t('monthly','Faturamento mensal')) : t('manual','Faturamento Manual')}
              </Text>
            </View>
            <View>
              <Text style={styles.fieldLabel}>{t('status','Status')}</Text>
              <View style={[styles.statusRowCentered, { alignSelf: 'flex-start' }]}>
                <View style={[styles.statusDot, { backgroundColor: getStatusColor(sub.status, theme) }]} />
                <Text style={styles.statusText}>
                  {String(t(`statusLabels.${sub.status}`, sub.status.charAt(0).toUpperCase() + sub.status.slice(1)))}
                </Text>
              </View>
            </View>
            <View>
              <Text style={styles.fieldLabel}>{t('nextInvoice','Próxima fatura')}</Text>
              {sub.cancel_at_period_end ? (
                <Text style={{ color: theme.colors.error, fontWeight: 'bold' }}>
                  {`${t('cancelAtPeriodEnd','Cancels on')} ${formatDate(sub.current_period_end, locale)}`}
                </Text>
              ) : (
                <Text>
                  {sub.status === 'trialing'
                    ? `${t('trialExpiresOn','Trial expires on')} ${formatDate(sub.next_payment_date, locale)} ${t('invoiceAmount','no valor de')} ${formatCurrency((sub.price?.unit_amount || 0) * sub.licenses, sub.price?.currency || 'BRL', locale)}`
                    : !sub.next_payment_date && !sub.price
                    ? ''
                    : `${t('nextInvoiceOn','Next invoice on')} ${formatDate(sub.next_payment_date, locale)} ${t('invoiceAmount','no valor de')} ${formatCurrency((sub.price?.unit_amount || 0) * sub.licenses, sub.price?.currency || 'BRL', locale)}`
                  }
                </Text>
              )}
            </View>
          </View>
        </Card.Content>
        <Card.Actions style={styles.mobileActions}>
          {sub.price && (
            <Button mode="text" icon="open-in-new" onPress={() => handleChangeLicenses(sub)} style={styles.mobileActionButton}>
              {t('manageSubscription', 'Manage Subscription')}
            </Button>
          )}
        </Card.Actions>
      </Card>
    );
  };

  return (
    <View key={screenSizeKey} style={styles.container}>
      {isLargeScreen && (
        <Sidebar
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={hasOwner}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver && hasOwner ? (newProfile) => updateProfile(newProfile) : undefined}
          onRegisterDriver={hasOwner && !hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
      <Surface style={[
        { flex: 1, backgroundColor: theme.colors.background },
        isLargeScreen && { marginLeft: SIDEBAR_WIDTH },
      ]}>
        <Appbar.Header>
          {!isLargeScreen && <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} />}
          <Appbar.Content title={t('title', 'Manage Licenses')} />
        </Appbar.Header>
        <ScrollView contentContainerStyle={styles.content}>
          {loading ? (
            <Text>{t('loading', 'Loading licenses...')}</Text>
          ) : isLargeScreen ? (
            <>
              {/* Buy Button */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <View style={{ flex: 1 }} />
                <Button
                  mode="contained"
                  onPress={() => setBuyModalVisible(true)}
                  style={[styles.buyButton, { marginLeft: 16, marginTop: 20, marginBottom: 8 }]}
                >
                  {t('buyLicenses', 'Buy Licenses')}
                </Button>
              </View>

              {/* GPS Tracking Licenses Section */}
              {gpsLicenses.length > 0 && (
                <>
                  <Text style={styles.sectionHeader}>{t('gpsLicensesSection', 'GPS Tracking Licenses')}</Text>
                  {gpsLicenses.map(sub => renderDesktopLicenseCard(sub))}
                  <View style={{ height: 24 }} />
                </>
              )}

              {/* Time Control Licenses Section */}
              {hoursLicenses.length > 0 && (
                <>
                  <Text style={styles.sectionHeader}>{t('hoursLicensesSection', 'Time Control Licenses')}</Text>
                  {hoursLicenses.map(sub => renderDesktopLicenseCard(sub))}
                </>
              )}

              {/* Combo Licenses Section */}
              {comboLicenses.length > 0 && (
                <>
                  <Text style={styles.sectionHeader}>{t('comboLicensesSection', 'Combo Licenses')}</Text>
                  {comboLicenses.map(sub => renderDesktopLicenseCard(sub))}
                </>
              )}

              {/* Empty state */}
              {gpsLicenses.length === 0 && hoursLicenses.length === 0 && (
                <Text style={{ textAlign: 'center', marginTop: 20, opacity: 0.6 }}>
                  {t('noLicenses', 'No active licenses')}
                </Text>
              )}
            </>
          ) : (
            <>
              {/* Mobile View: Buy Button */}
              <Button mode="contained" onPress={() => setBuyModalVisible(true)} style={[styles.buyButton, { alignSelf: 'flex-end' }]}>
                {t('buyLicenses', 'Buy Licenses')}
              </Button>
              <View style={{ height: 16 }} />

              {/* GPS Tracking Licenses Section */}
              {gpsLicenses.length > 0 && (
                <>
                  <Text style={styles.sectionHeader}>{t('gpsLicensesSection', 'GPS Tracking Licenses')}</Text>
                  {gpsLicenses.map(sub => renderMobileLicenseCard(sub))}
                  <View style={{ height: 24 }} />
                </>
              )}

              {/* Time Control Licenses Section */}
              {hoursLicenses.length > 0 && (
                <>
                  <Text style={styles.sectionHeader}>{t('hoursLicensesSection', 'Time Control Licenses')}</Text>
                  {hoursLicenses.map(sub => renderMobileLicenseCard(sub))}
                </>
              )}

              {/* Combo Licenses Section */}
              {comboLicenses.length > 0 && (
                <>
                  <Text style={styles.sectionHeader}>{t('comboLicensesSection', 'Combo Licenses')}</Text>
                  {comboLicenses.map(sub => renderMobileLicenseCard(sub))}
                </>
              )}

              {/* Empty state */}
              {gpsLicenses.length === 0 && hoursLicenses.length === 0 && comboLicenses.length === 0 && (
                <Text style={{ textAlign: 'center', marginTop: 20, opacity: 0.6 }}>
                  {t('noLicenses', 'No active licenses')}
                </Text>
              )}
            </>
          )}
        </ScrollView>
      </Surface>
      {!isLargeScreen && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={hasOwner}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver && hasOwner ? (newProfile) => updateProfile(newProfile) : undefined}
          onRegisterDriver={hasOwner && !hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
      <EditSubscriptionModal
        visible={!!editingSubscription}
        subscription={editingSubscription}
        onDismiss={() => setEditingSubscription(null)}
        onSuccess={() => { setEditingSubscription(null); fetchLicenses(); }}
      />
      <ChangePaymentMethodModal
        visible={!!changingPaymentMethod}
        subscription={changingPaymentMethod}
        onDismiss={() => setChangingPaymentMethod(null)}
        onSuccess={() => { setChangingPaymentMethod(null); fetchLicenses(); }}
      />
      <UnifiedBuyLicensesModal
        visible={buyModalVisible}
        onDismiss={() => setBuyModalVisible(false)}
        onSuccess={() => { setBuyModalVisible(false); fetchLicenses(); }}
      />
      <Portal>
        <Modal
          visible={showInvoices}
          onDismiss={() => setShowInvoices(false)}
          contentContainerStyle={[
            styles.modal, 
            { backgroundColor: theme.colors.background },
            Platform.OS === 'web' ? {
              maxWidth: 800,
              width: '90%',
              alignSelf: 'center',
            } : {}
          ]}
        >
          <ScrollView>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('invoices', 'Invoices')} - {getLocalizedProductName(selectedSubscription)}
              </Text>
              <IconButton
                icon="close"
                size={24}
                onPress={() => setShowInvoices(false)}
                style={styles.closeButton}
              />
            </View>
            {loadingInvoices ? (
              <Text>{t('loadingInvoices', 'Loading invoices...')}</Text>
            ) : invoices.length === 0 ? (
              <Text>{t('noInvoices', 'No invoices found')}</Text>
            ) : (
              <>
                {invoices.map((invoice) => (
                  <Card key={invoice.id} style={styles.invoiceCard}>
                    <Card.Content>
                      <View style={styles.invoiceRow}>
                        <View style={styles.invoiceInfo}>
                          <Text style={styles.invoiceDate}>
                            {new Date(invoice.created_at).toLocaleDateString()}
                          </Text>
                          <Text style={styles.invoiceAmount}>
                            {new Intl.NumberFormat('pt-PT', {
                              style: 'currency',
                              currency: invoice.currency.toUpperCase()
                            }).format(invoice.amount_paid)}
                          </Text>
                          <Chip
                            mode="outlined"
                            style={[
                              styles.invoiceStatusChip,
                              { backgroundColor: getStatusColor(invoice.status, theme) === theme.colors.primary ? theme.colors.primaryContainer : 
                                 getStatusColor(invoice.status, theme) === theme.colors.error ? theme.colors.errorContainer : 
                                 getStatusColor(invoice.status, theme) === theme.colors.secondary ? theme.colors.secondaryContainer : 
                                 theme.colors.surfaceVariant }
                            ]}
                            textStyle={{ 
                              color: getStatusColor(invoice.status, theme) === theme.colors.primary ? theme.colors.primary : 
                                     getStatusColor(invoice.status, theme) === theme.colors.error ? theme.colors.error : 
                                     getStatusColor(invoice.status, theme) === theme.colors.secondary ? theme.colors.secondary : 
                                     theme.colors.onSurfaceVariant 
                            }}
                          >
                            {t(`statusLabels.${invoice.status}`, invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1))}
                          </Chip>
                        </View>
                        <View style={styles.invoiceActions}>
                          {invoice.invoice_pdf && (
                            <Button
                              mode="outlined"
                              onPress={() => handleOpenInvoice(invoice.invoice_pdf)}
                              style={styles.invoiceButton}
                            >
                              {t('download', 'Download')}
                            </Button>
                          )}
                          {invoice.hosted_invoice_url && (
                            <Button
                              mode="contained"
                              onPress={() => handleOpenInvoice(invoice.hosted_invoice_url)}
                              style={styles.invoiceButton}
                            >
                              {t('view', 'View')}
                            </Button>
                          )}
                        </View>
                      </View>
                    </Card.Content>
                  </Card>
                ))}
                <View style={styles.paginationContainer}>
                  <Text style={styles.paginationInfo}>
                    {totalInvoices > 0 ? (
                      `${t('showing', 'Showing')} ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalInvoices)}-${Math.min(currentPage * ITEMS_PER_PAGE, totalInvoices)} ${t('of', 'of')} ${totalInvoices}`
                    ) : (
                      t('noInvoices', 'No invoices found')
                    )}
                  </Text>
                  {totalPages > 1 && (
                    <View style={styles.paginationControls}>
                      <Button
                        mode="outlined"
                        onPress={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage <= 1}
                        style={styles.paginationButton}
                      >
                        {t('previous', 'Previous')}
                      </Button>
                      <Text style={styles.pageNumber}>
                        {t('page', 'Page')} {currentPage} {t('of', 'of')} {totalPages}
                      </Text>
                      <Button
                        mode="outlined"
                        onPress={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        style={styles.paginationButton}
                      >
                        {t('next', 'Next')}
                      </Button>
                    </View>
                  )}
                </View>
              </>
            )}
          </ScrollView>
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    padding: 20,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  subDetails: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 4,
  },
  detailLabel: {
    fontWeight: 'bold',
  },
  detailValue: {},
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  summary: {
    marginVertical: 16,
  },
  buyButton: {
    marginTop: 20,
  },
  toggleButton: {
    marginBottom: 16,
  },
  actionButton: {
    marginTop: 8,
    minWidth: 110,
    height: 36,
    paddingHorizontal: 12,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  summaryCard: {
    marginTop: 16,
  },
  sectionHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    marginTop: 8,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  subscriptionCard: {
    marginBottom: 16,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  desktopRowTop: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  desktopRowBottom: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardColProduct: {
    flex: 2.2,
    paddingRight: 8,
  },
  cardColFrequency: {
    flex: 1.2,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  cardColPaymentType: {
    flex: 0.7,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  cardColStatus: {
    flex: 0.8,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  cardColNextInvoice: {
    flex: 2,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  nextInvoiceArea: {
    flex: 1,
    paddingRight: 12,
  },
  actionsArea: {
    alignItems: 'flex-end',
    minWidth: 160,
  },
  cardColActions: {
    flex: 1.2,
    alignItems: 'center',
    paddingHorizontal: 4,
    minWidth: 120,
  },
  loadingIndicator: {
    marginRight: 8,
  },
  productText: {
    flex: 1,
  },
  statusChip: {
    marginVertical: 4,
  },
  statusBadge: {
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    minWidth: 90,
    alignItems: 'center',
  },
  statusRowCentered: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  statusText: {
    fontWeight: '600',
  },
  frequencyText: {
    fontWeight: 'bold',
  },
  paymentTypeText: {
    fontWeight: 'bold',
  },
  nextInvoiceText: {
    fontWeight: 'bold',
  },
  expiresText: {
    fontStyle: 'italic',
  },
  headerRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginBottom: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  headerText: {
    fontWeight: 'bold',
  },
  fieldLabel: {
    fontSize: 12,
    opacity: 0.8,
    marginBottom: 4,
  },
  modal: {
    margin: 20,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
  },
  closeButton: {
    margin: 0,
  },
  invoiceCard: {
    marginBottom: 10,
  },
  invoiceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invoiceInfo: {
    flex: 1,
  },
  invoiceDate: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  invoiceAmount: {
    fontSize: 14,
    marginVertical: 4,
  },
  invoiceStatusChip: {
    marginVertical: 4,
  },
  invoiceActions: {
    flexDirection: 'row',
    gap: 8,
  },
  invoiceButton: {
    marginLeft: 8,
  },
  paginationContainer: {
    marginTop: 20,
    padding: 10,
    alignItems: 'center',
  },
  paginationInfo: {
    marginBottom: 10,
    color: '#666',
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  paginationButton: {
    minWidth: 100,
  },
  pageNumber: {
    marginHorizontal: 10,
    fontWeight: 'bold',
  },
  mobileDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  mobileActionButton: {
    marginRight: 8,
    marginBottom: 8,
  },
});